import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { initiativesAPI } from '../../lib/api';
import { UnifiedContentEditor, SidebarCard } from '../../components/admin/UnifiedContentEditor';
import { CitationPicker } from '../../components/admin/CitationPicker';
import { ReferencesManager } from '../../components/admin/ReferencesManager';

export default function InitiativeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const editorRef = useRef(null);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  
  // Core content
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  
  // Metadata
  const [status, setStatus] = useState('ongoing');
  
  // Citations
  const [citations, setCitations] = useState([]);
  const [showCitationPicker, setShowCitationPicker] = useState(false);

  useEffect(() => {
    if (isEditing) {
      const fetchInitiative = async () => {
        try {
          const response = await initiativesAPI.getOne(id);
          const data = response.data;
          setTitle(data.title || '');
          setDescription(data.description || '');
          setContent(data.content || '');
          setFeaturedImage(data.featured_image || '');
          setStatus(data.status || 'ongoing');
          setCitations(data.citations || []);
        } catch (error) {
          toast.error('Failed to load initiative');
          navigate('/admin/initiatives');
        } finally {
          setLoading(false);
        }
      };
      fetchInitiative();
    }
  }, [id, isEditing, navigate]);

  // Handle selecting a citation from the picker
  const handleSelectCitation = (citationIndex) => {
    if (editorRef.current) {
      editorRef.current.insertCitation(citationIndex);
    }
  };

  // Handle adding a new citation
  const handleAddCitation = (citationHtml) => {
    setCitations([...citations, citationHtml]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      toast.error('Please fill in title and description');
      return;
    }

    setSaving(true);

    const formData = {
      title,
      description,
      content,
      featured_image: featuredImage,
      status,
      citations
    };

    try {
      if (isEditing) {
        await initiativesAPI.update(id, formData);
        toast.success('Initiative updated successfully');
      } else {
        await initiativesAPI.create(formData);
        toast.success('Initiative created successfully');
      }
      navigate('/admin/initiatives');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save initiative');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  // Sidebar content for initiatives
  const sidebarContent = (
    <>
      <SidebarCard title="Status">
        <div className="space-y-2">
          <Label className="text-sm">Initiative Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="initiative-status" className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="ongoing">Ongoing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Set the current status of this initiative.
          </p>
        </div>
      </SidebarCard>

      {/* References Manager */}
      <SidebarCard title="">
        <ReferencesManager
          citations={citations}
          onCitationsChange={setCitations}
          maxHeight="200px"
        />
      </SidebarCard>
    </>
  );

  return (
    <>
      <UnifiedContentEditor
        ref={editorRef}
        contentType="initiative"
        pageTitle={isEditing ? 'Edit Initiative' : 'New Initiative'}
        
        // Core content
        title={title}
        onTitleChange={setTitle}
        titlePlaceholder="Enter initiative title..."
        
        // Description (similar to excerpt)
        showExcerpt={true}
        excerpt={description}
        onExcerptChange={setDescription}
        excerptPlaceholder="Brief description of this initiative..."
        excerptHelp="This description appears in initiative listings and previews."
        
        // Content
        content={content}
        onContentChange={setContent}
        contentPlaceholder="Write detailed information about this initiative..."
        
        // Featured image
        featuredImage={featuredImage}
        onFeaturedImageChange={setFeaturedImage}
        
        // Actions
        onBack={() => navigate('/admin/initiatives')}
        onSubmit={handleSubmit}
        saving={saving}
        isEditing={isEditing}
        submitLabel={isEditing ? 'Update Initiative' : 'Create Initiative'}
        
        // Sidebar
        sidebarContent={sidebarContent}
        
        // Citation support
        citations={citations}
        onInsertCitation={() => setShowCitationPicker(true)}
        showCitationButton={true}
      />

      {/* Citation Picker Modal */}
      <CitationPicker
        open={showCitationPicker}
        onOpenChange={setShowCitationPicker}
        citations={citations}
        onSelectCitation={handleSelectCitation}
        onAddCitation={handleAddCitation}
      />
    </>
  );
}
